import React from "react";
import { Box, Container, Grid, Paper, Typography } from "@mui/material";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremium";
import HandshakeRoundedIcon from "@mui/icons-material/HandshakeRounded";
import { motion } from "framer-motion";

const MotionPaper = motion(Paper);

export default function ParentSection() {
  const cards = [
    {
      icon: <FavoriteRoundedIcon sx={{ fontSize: 42 }} />,
      title: "A Parent's Peace of Mind",
      subtitle: "Because Every Parent Wants the Best for Their Child",
      description:
        "Every parent dreams of seeing their child succeed with confidence and happiness. We provide a supportive learning environment where students receive personalized attention, build strong academic foundations, and grow into confident lifelong learners.",
      color: "#FF6B6B",
    },
    {
      icon: <WorkspacePremiumRoundedIcon sx={{ fontSize: 42 }} />,
      title: "Our Promise",
      subtitle: "Every Child Matters. Every Dream Matters.",
      description:
        "We believe every child deserves encouragement, patience, and the right mentor to reach their full potential. Our mission is to inspire confidence, nurture curiosity, and guide every learner toward lasting academic and personal success.",
      color: "#1976D2",
    },
    {
      icon: <HandshakeRoundedIcon sx={{ fontSize: 42 }} />,
      title: "Our Commitment",
      subtitle: "Supporting Every Step of the Learning Journey",
      description:
        "We are committed to creating a learning environment where students feel motivated, supported, and empowered to achieve excellence through quality education, personalized guidance, and continuous encouragement.",
      color: "#2E7D32",
    },
  ];

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: "linear-gradient(180deg,#F8FBFF 0%,#EEF6FF 100%)",
      }}
    >
      <Container maxWidth="xl">
        {/* Heading */}

        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          textAlign="center"
          mb={8}
        >
          <Typography
            sx={{
              color: "#1976D2",
              fontWeight: 700,
              letterSpacing: 2,
              textTransform: "uppercase",
              mb: 2,
            }}
          >
            Why Parents Choose Us
          </Typography>

          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              color: "#1E293B",
              mb: 3,
              fontSize: {
                xs: "1.5rem",
                sm: "1.75rem",
                md: "2rem",
              },
            }}
          >
            Supporting Every Child's Success
          </Typography>

          <Typography
            sx={{
              maxWidth: "850px",
              mx: "auto",
              color: "#64748B",
              lineHeight: 1.9,
            }}
          >
          </Typography>
        </Box>

        {/* Cards */}

        <Grid
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          container
          spacing={{ xs: 2.5, sm: 3, md: 4 }}
          justifyContent="center"
          alignItems="stretch"
        >
          {cards.map((card, index) => (
            <Grid
              key={index}
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <MotionPaper
                variants={cardVariants}
                whileHover={{ y: -10, boxShadow: "0 20px 45px rgba(0,0,0,0.08)" }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                elevation={0}
                sx={{
                  height: "100%",
                  p: { xs: 3, sm: 4, md: 5 },
                  borderRadius: "24px",
                  background: "#fff",
                  border: "1px solid #E2E8F0",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    width: 55,
                    height: 55,
                    borderRadius: "20px",
                    bgcolor: card.color,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 3,
                  }}
                >
                  {card.icon}
                </Box>

                <Typography
                  variant="h5"
                  fontWeight={700}
                  color="#1E293B"
                  mb={1}
                >
                  {card.title}
                </Typography>

                <Typography
                  sx={{
                    color: card.color,
                    fontWeight: 600,
                    mb: 1,
                    fontSize: "1rem",
                  }}
                >
                  {card.subtitle}
                </Typography>

                <Typography
                  sx={{
                    color: "#64748B",
                    lineHeight: 1.9,
                    flexGrow: 1,
                    fontSize: "14px",
                  }}
                >
                  {card.description}
                </Typography>
              </MotionPaper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}